'use client';

import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './Tabs.css';

export interface TabsItem {
  id: string;
  label: React.ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabsItem[];
  value?: string | null;
  defaultValue?: string | null;
  onValueChange?: (value: string | null) => void;
  className?: string;
  ariaLabel?: string;
  size?: 'sm' | 'md' | 'lg';
}

interface IndicatorRect {
  left: number;
  width: number;
}

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Tabs navigation component with an animated underline indicator.
 * The indicator is a single element positioned via measured offsets and
 * animated with a CSS transition, which keeps it immune to unrelated
 * parent re-renders (e.g. drawer drag-state toggles).
 */
export const Tabs = React.memo(function Tabs({
  items,
  value,
  defaultValue = null,
  onValueChange,
  className = '',
  ariaLabel,
  size = 'md',
}: TabsProps) {
  const reactId = useId();
  const baseId = useMemo(() => `tabs-${reactId}`, [reactId]);

  const [internalValue, setInternalValue] = useState<string | null>(
    value ?? defaultValue ?? items[0]?.id ?? null,
  );

  const selected = value ?? internalValue;

  const listRef = useRef<HTMLUListElement | null>(null);
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [indicator, setIndicator] = useState<IndicatorRect | null>(null);
  const [isReady, setIsReady] = useState(false);

  const setButtonRef = useCallback(
    (id: string) => (node: HTMLButtonElement | null) => {
      if (node) {
        buttonRefs.current.set(id, node);
      } else {
        buttonRefs.current.delete(id);
      }
    },
    [],
  );

  const measure = useCallback(() => {
    const list = listRef.current;
    if (!list || !selected) return;
    const button = buttonRefs.current.get(selected);
    if (!button) return;
    const listRect = list.getBoundingClientRect();
    const btnRect = button.getBoundingClientRect();
    setIndicator({ left: btnRect.left - listRect.left, width: btnRect.width });
  }, [selected]);

  useIsomorphicLayoutEffect(() => {
    measure();
    // Enable transitions only after the first measurement, so the initial
    // placement doesn't animate in from 0.
    const raf = requestAnimationFrame(() => setIsReady(true));
    return () => cancelAnimationFrame(raf);
  }, [measure]);

  useEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(list);
    buttonRefs.current.forEach((btn) => ro.observe(btn));
    return () => ro.disconnect();
  }, [measure, items]);

  const handleSelect = useCallback(
    (next: string) => {
      if (value === undefined) setInternalValue(next);
      onValueChange?.(next);
    },
    [onValueChange, value],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLUListElement>) => {
      const currentIndex = items.findIndex((i) => i.id === selected);
      if (currentIndex === -1) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        const nextIndex = (currentIndex + 1) % items.length;
        handleSelect(items[nextIndex].id);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        const nextIndex = (currentIndex - 1 + items.length) % items.length;
        handleSelect(items[nextIndex].id);
      } else if (event.key === 'Home') {
        event.preventDefault();
        handleSelect(items[0].id);
      } else if (event.key === 'End') {
        event.preventDefault();
        handleSelect(items[items.length - 1].id);
      }
    },
    [handleSelect, items, selected],
  );

  return (
    <nav className={`tabs ${className}`} aria-label={ariaLabel} data-size={size}>
      <ul
        ref={listRef}
        className="tabs__list"
        role="tablist"
        onKeyDown={onKeyDown}
      >
        {items.map((item) => {
          const isActive = selected === item.id;
          const tabId = `${baseId}-tab-${item.id}`;
          return (
            <li
              key={item.id}
              className={`tabs__item${isActive ? ' is-active' : ''}`}
              role="presentation"
            >
              <button
                ref={setButtonRef(item.id)}
                id={tabId}
                type="button"
                className="tabs__button"
                role="tab"
                aria-selected={isActive}
                aria-controls={undefined}
                disabled={item.disabled}
                data-state={isActive ? 'active' : 'inactive'}
                onClick={() => handleSelect(item.id)}
              >
                {item.label}
              </button>
            </li>
          );
        })}
        {indicator ? (
          <span
            aria-hidden="true"
            className="tabs__indicator"
            data-ready={isReady ? 'true' : 'false'}
            style={{
              transform: `translateX(${indicator.left}px)`,
              width: indicator.width,
            }}
          />
        ) : null}
      </ul>
    </nav>
  );
});

Tabs.displayName = 'Tabs';

export default Tabs;
